import { submitFormJson } from 'widgets/toolbox/ajax';

import { TRefElementInstance } from 'widgets/toolbox/RefElement';
import { TBasicInputInstance } from 'widgets/forms/BasicInput';

/**
 * @description Base CouponForm implementation
 * @param AjaxForm Base widget for extending
 * @returns Coupon Form class
 */
function CouponFormClassCreator(AjaxForm: import('widgets/forms/AjaxForm').TAjaxForm) {
    /**
     * @category widgets
     * @subcategory forms
     * @class CouponForm
     * @augments AjaxForm
     * @classdesc Cart page promo code application AJAX form.
     * Used to apply/delete coupons for current cart.
     * Widget also communicates application results (either success or error) to customer.
     * When coupon application form is opened, sets focus on coupon input.
     * @property {string} data-widget - Widget name `couponform`
     * @property {string} data-error-message-label - Specific error message label
     * @property {string} data-event-submit - Event listener for submit form event
     * @example <caption>Example of CouponForm widget markup</caption>
     * <form
     *     name="promo-code-form"
     *     novalidate
     *     action="{{actionUrls.submitCouponCodeUrl}}"
     *     method="POST"
     *     data-remove-coupon-url="${pdict.actionUrls.removeCouponLineItem}"
     *     data-widget="couponform"
     *     data-error-message-label="errorFeedback"
     *     data-event-submit.prevent="handleSubmit"
     * >
     *     <isprint value="${
     *         formElement(require('server').forms.getForm('coupon').couponCode).render()
     *     }" encoding="off"/>
     *     <button
     *         type="submit"
     *         class="b-button m-outline"
     *         data-widget="button"
     *         data-widget-event-click="handleSubmit"
     *         data-event-click.prevent="handleClick"
     *         data-id="submitButton"
     *         data-tau="coupon_submit"
     *     >
     *         ${Resource.msg('form.promoCode.submit', 'forms', null)}
     *     </button>
     *     <div class="b-coupon_form-error" data-ref="couponDeletionMsg" role="alert" hidden></div>
     *     <isinclude template="m/cart/cartCouponDisplay" />
     * </form>
     */
    class CouponForm extends AjaxForm {
        prefs() {
            return {
                msgEmptyCoupon: '',
                couponInput: 'dwfrm_coupon_couponCode',
                couponCodeContent: 'couponCodeContent',
                couponDeletionMsg: 'couponDeletionMsg',
                removeCouponUrl: '',
                classesShow: 'show',
                ...super.prefs()
            };
        }

        /**
         * @description `onSubmitted` handler - communicates either error or success coupon application status
         * Notifies {@link CartMgr} about coupon status changes
         * @param data server JSON response
         * @param data.error server JSON response error
         * @emits "coupon.updated"
         */
        onSubmitted(data: TServerResponse) {
            if (!data) {
                return;
            }

            if (!data.success && data.errorMessage) {
                this.getById(this.prefs().couponInput, (input: TBasicInputInstance) => input.setError(data.errorMessage));
            } else if (!data.success && data.fieldErrors) {
                Object.entries(data.fieldErrors).forEach(([field, errorMessage]) => {
                    this.getById(field, (input: TBasicInputInstance) => input.setError(errorMessage));
                });
            } else {
                this.getById(this.prefs().couponInput, (input: TBasicInputInstance) => input.setValue('', true));
                /**
                 * @description Event to notify concerned widgets, that coupon was updated
                 * @event "coupon.updated"
                 */
                this.eventBus().emit('coupon.updated', data);
            }
        }

        /**
         * @description Invalidates coupon code input with relevant error message
         * @param error - error, happened during request
         */
        onError(error: Error) {
            this.getById(this.prefs().couponInput, (input: TBasicInputInstance) => input.setError(error.message));
        }

        /**
         * @description Removes coupon code from cart
         * @param button - triggering button
         * @emits "coupon.updated"
         * @returns Promise object represents server response for coupon deletion
         */
        removeCoupon(button: TRefElementInstance) {
            this.busy();
            this.has(this.prefs().couponDeletionMsg, function (errMsg) {
                errMsg.setText('').hide();
            });

            const couponCode = button.data<string>('code');

            return submitFormJson(this.prefs().removeCouponUrl, {
                uuid: button.data<string>('uuid')
            })
                .then((response) => {
                    if (!response.success && response.errorMessage) {
                        this.has(this.prefs().couponDeletionMsg, function (errMsg) {
                            errMsg.setText(<string>response.errorMessage).show();
                        });

                        return response;
                    }

                    response.couponAction = 'removed';
                    response.couponCode = couponCode;

                    this.eventBus().emit('coupon.updated', response);

                    return response;
                })
                .finally(() => {
                    this.unbusy();
                });
        }

        /**
         * @description Sets focus on coupon input. Usually happens when coupon form is opened.
         */
        focusInput() {
            this.getById(this.prefs().couponInput, (input: TBasicInputInstance) => input.focus());
        }
    }

    return CouponForm;
}

export type TCouponForm = ReturnType<typeof CouponFormClassCreator>;

export type TCouponFormInstance = InstanceType<TCouponForm>;

export default CouponFormClassCreator;
